import { FC, useEffect, useMemo, useState } from "react";
import { guid } from "odata-query";
import { makeStyles } from "tss-react/mui";
import { MiInputTextbox, MiList, MiModalConfirm } from "@miplatform/components";
import {
  BUTTON_LABELS,
  CONFIG_PAGES,
  HTTP_STATUSES,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useDebounce, useRouter } from "@miplatform/hooks";
import { IRoleListItemResponse, IVerticalTabProps } from "@miplatform/types";
import { AddButton, VerticalTabsLayout } from "@mibuildweb/components";
import {
  useDepartments,
  usePermissions,
  useRole,
  useRoles,
} from "@mibuildweb/hooks";
import { CONFIG_PATHS } from "../../../routes/config/constants";
import { ConfigPageLayout } from "../ConfigPageLayout";
import { ConfigRoleCard } from "../components";

export const ConfigRoleList: FC<ConfigRoleListProps> = ({
  modulePermission,
}) => {
  const { classes } = useStyles();
  const { navigate, pathname } = useRouter();
  const [selectedRoleToDelete, setSelectedRoleToDelete] =
    useState<IRoleListItemResponse | null>(null);
  const [selectedRoleToCopy, setSelectedRoleToCopy] =
    useState<IRoleListItemResponse | null>(null);
  const [roleName, setRoleName] = useState("");

  const handleRoleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRoleName(event.target.value);
  };

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  const { hasPermission } = usePermissions();
  const canCreateRole = hasPermission(PERMISSIONS.ROLE.CREATE);
  const canUpdateRole = hasPermission(PERMISSIONS.ROLE.UPDATE);
  const canDeleteRole = hasPermission(PERMISSIONS.ROLE.DELETE);

  const {
    roles,
    getRoles,
    setRoles,
    loading,
    recentRoles,
    getRecentRoles,
    setRecentRoles,
    filter,
    setFilter,
  } = useRoles({ debouncedSearch, modulePermission });
  const { deleteRole, duplicateRole, error: roleError } = useRole();
  const { departments, getDepartments } = useDepartments();

  const [triggerRefresh, setTriggerRefresh] = useState(false);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleTabChange = (
    tab?: IVerticalTabProps<IRoleListItemResponse[]> | null
  ) => {
    setFilter(tab?.filter || {});
    setSearch("");
  };

  const refreshData = () => {
    setTriggerRefresh((prev) => !prev);
  };

  const navigateToRoleDetails = (roleId: string) => {
    navigate(`${pathname}/${roleId}`);
  };

  const navigateToAddRole = () => {
    navigate(`${pathname}/add-role`);
  };

  const handleDeleteRole = async (role: IRoleListItemResponse | null) => {
    if (role) {
      await deleteRole(role.id, role.modifiedOn);
      setSelectedRoleToDelete(null);
      refreshData();
    }
  };

  const handleDuplicateRole = async (roleName: string) => {
    if (selectedRoleToCopy && roleName.trim() !== "") {
      const response = await duplicateRole(roleName, selectedRoleToCopy.id);

      if (response?.status === HTTP_STATUSES.OK) {
        setSelectedRoleToCopy(null);
        setRoleName("");
        refreshData();
      }
    }
  };

  const handleSelectRoleToCopy = (role: IRoleListItemResponse) => {
    setSelectedRoleToCopy(role);
    setRoleName(`${role.name}`);
  };

  const handleCancelRoleCopy = () => {
    setSelectedRoleToCopy(null);
    setRoleName("");
  };

  const duplicateButtonDisabled =
    roleName.trim() === "" || roleName === selectedRoleToCopy?.name;

  const duplicateButtonError = useMemo<Error | null>(() => {
    if (roleName.trim() === "") {
      return new Error("Role name is required");
    }
    if (roleName.trim() === selectedRoleToCopy?.name) {
      return new Error(
        "Please provide a different name for the duplicated role"
      );
    }
    return null;
  }, [roleName, selectedRoleToCopy]);

  const departmentTabs = useMemo(() => {
    const tabs = [];
    const deptTabs = departments.map((department) => ({
      label: department.name,
      value: department.id,
      filter: { departmentId: { eq: guid(department.id) } },
    }));
    tabs.push({ label: "All", value: "All", filter: {} });
    return tabs.concat(deptTabs);
  }, [departments]);

  const refreshRoles = useMemo(() => {
    return { filter, triggerRefresh, pathname };
  }, [filter, triggerRefresh, pathname]);

  useEffect(() => {
    getDepartments();
  }, [pathname]);

  return (
    <ConfigPageLayout
      title="Roles & Permissions"
      loading={loading}
      titleActionComponent={
        canCreateRole &&
        pathname !== CONFIG_PATHS[CONFIG_PAGES.ROLES_OPERATIONS] && (
          <AddButton onClick={navigateToAddRole} label="Add New Role" />
        )
      }
      search={search}
      onHandleSearch={handleSearchChange}
      searchPlaceholder="Search"
      hideOnBack
    >
      <VerticalTabsLayout
        hideStatusIcons
        tabs={departmentTabs}
        onTabChange={handleTabChange}
        hideNavigationButtons
      >
        <MiList
          viewType="card"
          data={roles}
          dataProvider={getRoles}
          loading={loading}
          setData={setRoles}
          recentData={recentRoles}
          setRecentData={setRecentRoles}
          recentDataProvider={getRecentRoles}
          CardComponent={ConfigRoleCard}
          cardProps={{
            navigateToRoleDetails,
            onClick: navigateToRoleDetails,
            canDeleteRole,
            canUpdateRole,
            canCreateRole,
            handleDeleteRole: setSelectedRoleToDelete,
            handleDuplicateRole: handleSelectRoleToCopy,
          }}
          paginationMode={PAGINATION.SERVER}
          refresh={refreshRoles}
          entityName="Roles"
          emptyListProps={{
            text: "No Roles found",
          }}
          search={debouncedSearch}
        />
      </VerticalTabsLayout>
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        textPositive={BUTTON_LABELS.DELETE}
        open={!!selectedRoleToDelete}
        handleNegative={() => setSelectedRoleToDelete(null)}
        handlePositive={() => {
          handleDeleteRole(selectedRoleToDelete);
        }}
        description={`You are about to delete the ${selectedRoleToDelete?.name} role. Are you sure you would like to continue?`}
        buttonPositiveColor="error"
      />
      {canCreateRole && selectedRoleToCopy && (
        <MiModalConfirm
          open={!!selectedRoleToCopy}
          title={`Duplicate Role: ${selectedRoleToCopy.name}`}
          textPositive="Create Duplicate"
          handleNegative={handleCancelRoleCopy}
          handlePositive={() => handleDuplicateRole(roleName)}
          description={`Creating a duplicate of the ${selectedRoleToCopy.name} role. Please provide a name for the new role.`}
          disableClick={duplicateButtonDisabled}
        >
          <MiInputTextbox
            name="roleName"
            label="New Role Name"
            value={roleName}
            onChange={handleRoleNameChange}
            placeholder={selectedRoleToCopy.name}
            inputProps={{
              autoFocus: true,
            }}
            className={classes.duplicateNameInput}
            required
            error={roleError || duplicateButtonError}
          />
        </MiModalConfirm>
      )}
    </ConfigPageLayout>
  );
};

interface ConfigRoleListProps {
  modulePermission?: string;
}

const useStyles = makeStyles()((theme) => ({
  duplicateNameInput: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
}));
